/**
 * Service de notification des fallbacks
 * Gère l'affichage des alertes utilisateur lors de l'utilisation de valeurs de fallback
 */

import { apiFetch } from './httpClient';

export interface FallbackAlert {
  id: string;
  type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  fallbackValue?: string;
  context?: string;
  timestamp: Date;
  onRetry?: () => void;
  onDismiss?: () => void;
}

class FallbackNotificationService {
  private static activeAlerts: FallbackAlert[] = [];
  private static alertListeners: ((alerts: FallbackAlert[]) => void)[] = [];

  /**
   * Affiche une alerte de fallback
   */
  static showFallbackAlert(
    type: string, 
    message: string, 
    severity: 'low' | 'medium' | 'high' | 'critical' = 'medium',
    options: {
      fallbackValue?: string;
      context?: string;
      onRetry?: () => void;
      onDismiss?: () => void;
    } = {}
  ): void {
    console.log(`🚨 FALLBACK ${severity.toUpperCase()}: ${type}`, { message, ...options });
    
    // Générer un ID unique avec timestamp pour éviter les doublons
    const alertId = `${type}-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;
    
    const alert: FallbackAlert = {
      id: alertId,
      type,
      message,
      severity,
      timestamp: new Date(),
      fallbackValue: options.fallbackValue,
      context: options.context,
      onRetry: options.onRetry,
      onDismiss: () => {
        this.removeAlert(alert.id);
        if (options.onDismiss) {
          options.onDismiss();
        }
      }
    };

    // Ajouter l'alerte à la liste active
    this.activeAlerts.push(alert);
    
    // Notifier les abonnés
    this.notifySubscribers();
    
    // Synchroniser avec le backend (optionnel)
    this.syncWithBackend(alert);
    
    // Auto-suppression après délai (sauf pour les alertes critiques)
    if (severity !== 'critical') {
      setTimeout(() => {
        this.removeAlert(alert.id);
      }, this.getAutoRemoveDelay(severity));
    }
  }

  /**
   * Supprime une alerte
   */
  private static removeAlert(alertId: string): void {
    const index = this.activeAlerts.findIndex(alert => alert.id === alertId);
    if (index >= 0) {
      this.activeAlerts.splice(index, 1);
      this.notifySubscribers();
    }
  }

  /**
   * Notifie tous les abonnés
   */
  private static notifySubscribers(): void {
    this.alertListeners.forEach(listener => {
      try {
        listener([...this.activeAlerts]);
      } catch (error) {
        console.error('Erreur notification listener:', error);
      }
    });
  }

  /**
   * S'abonne aux alertes actives
   */
  static subscribeToAlerts(listener: (alerts: FallbackAlert[]) => void): () => void {
    this.alertListeners.push(listener);
    
    // Envoyer les alertes actuelles
    listener([...this.activeAlerts]);
    
    // Retourner la fonction de désabonnement
    return () => {
      const index = this.alertListeners.indexOf(listener);
      if (index >= 0) {
        this.alertListeners.splice(index, 1);
      }
    };
  }

  /**
   * Récupère le délai d'auto-suppression selon la sévérité
   */
  private static getAutoRemoveDelay(severity: string): number {
    switch (severity) {
      case 'low': return 3000;    // 3 secondes
      case 'medium': return 5000; // 5 secondes
      case 'high': return 8000;   // 8 secondes
      case 'critical': return 0;  // Pas d'auto-suppression
      default: return 5000;
    }
  }

  /**
   * Synchronise avec le backend
   */
  private static async syncWithBackend(alert: FallbackAlert): Promise<void> {
    try {
      await apiFetch('/fallback/notification', {
        method: 'POST',
        body: JSON.stringify({
          type: alert.type,
          severity: alert.severity,
          message: alert.message,
          fallback_value: alert.fallbackValue,
          context: alert.context,
          timestamp: alert.timestamp.toISOString()
        })
      });
    } catch (error) {
      console.warn('Echec synchronisation fallback avec backend:', error);
    }
  }

  /**
   * Efface toutes les alertes actives
   */
  static clearActiveAlerts(): void {
    this.activeAlerts = [];
    this.notifySubscribers();
  }

  /**
   * Récupère les alertes actives
   */
  static getActiveAlerts(): FallbackAlert[] {
    return [...this.activeAlerts];
  }
}

export default FallbackNotificationService;

// Fonctions utilitaires pour les types de fallback spécifiques
export const FallbackAlerts = {
  /**
   * Alerte TVA fallback
   */
  tva: (fallbackRate: number = 20, context?: string) => {
    FallbackNotificationService.showFallbackAlert(
      'tva',
      `Impossible de récupérer les données TVA de l'entreprise. Taux ${fallbackRate}% appliqué par défaut.`,
      'critical',
      {
        fallbackValue: `${fallbackRate}%`,
        context: context || 'Calcul TVA entreprise'
      }
    );
  },

  /**
   * Alerte fermetures fallback
   */
  fermetures: (fallbackPrice: number = 8.5, context?: string) => {
    FallbackNotificationService.showFallbackAlert(
      'fermetures',
      `Service fermetures indisponible. Prix fixe ${fallbackPrice}€ appliqué.`,
      'high',
      {
        fallbackValue: `${fallbackPrice}€`,
        context: context || 'Calcul fermetures adaptatives'
      }
    );
  },

  /**
   * Alerte matières fallback
   */
  matieres: (context?: string) => {
    FallbackNotificationService.showFallbackAlert(
      'matieres',
      'Impossible de récupérer les matières depuis le serveur. Matières de démonstration utilisées.',
      'medium',
      {
        fallbackValue: 'Matières démo',
        context: context || 'Récupération matières'
      }
    );
  },

  /**
   * Alerte clients fallback
   */
  clients: (context?: string) => {
    FallbackNotificationService.showFallbackAlert(
      'clients',
      'Impossible de récupérer les clients depuis le serveur. Données locales utilisées.',
      'medium',
      {
        fallbackValue: 'Données locales',
        context: context || 'Récupération clients'
      }
    );
  },

  /**
   * Alerte calcul prix fallback
   */
  calculPrix: (context?: string) => {
    FallbackNotificationService.showFallbackAlert(
      'calcul',
      'Erreur de calcul serveur. Calcul local utilisé - vérifiez les résultats.',
      'critical',
      {
        fallbackValue: 'Calcul local',
        context: context || 'Calcul prix article'
      }
    );
  },

  /**
   * Alerte sauvegarde fallback
   */
  sauvegarde: (context?: string) => {
    FallbackNotificationService.showFallbackAlert(
      'sauvegarde',
      'Échec sauvegarde serveur. Données sauvegardées localement.',
      'high',
      {
        fallbackValue: 'Sauvegarde locale',
        context: context || 'Sauvegarde données'
      }
    );
  },

  /**
   * Alerte export fallback
   */
  export: (format: string, context?: string) => {
    FallbackNotificationService.showFallbackAlert(
      'export',
      `Échec export ${format} serveur. Export local utilisé.`,
      'medium',
      {
        fallbackValue: 'Export local',
        context: context || `Export ${format}`
      }
    );
  },

  /**
   * Alerte devis fallback
   */
  devis: (context?: string) => {
    FallbackNotificationService.showFallbackAlert(
      'devis',
      'Impossible de récupérer les devis depuis le serveur. Données locales utilisées.',
      'medium',
      {
        fallbackValue: 'Données locales',
        context: context || 'Récupération devis'
      }
    );
  }
};
